import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackLeadGeneration } from "@/lib/analytics";
import { Loader2, Check } from "lucide-react";
import { z } from "zod";

const emailSchema = z.object({
  email: z.string().trim().email({ message: "Please enter a valid email address" }).max(255),
});

const LoyaltySignupForm = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email
    const validation = emailSchema.safeParse({ email });
    if (!validation.success) {
      toast({
        title: "Invalid email",
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the secure edge function instead of direct database insert
      const { data, error } = await supabase.functions.invoke('join-loyalty-program', {
        body: { email: email.trim().toLowerCase() }
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || data.errors?.join(', ') || 'Failed to join program');
      }

      if (data.alreadyEnrolled) {
        toast({
          title: "Already enrolled! 🎉",
          description: data.message,
        });
      } else {
        // Track conversion
        trackLeadGeneration('contact_form', { 
          source: 'homepage_loyalty_signup',
        });

        toast({
          title: "🎉 Welcome to Pawsome Rewards!",
          description: "You've earned 50 bonus points! Check your email for details.",
          duration: 5000,
        });
      }

      setIsSuccess(true);
      setEmail("");
      
      // Reset success state after 3 seconds
      setTimeout(() => setIsSuccess(false), 3000);

    } catch (error) {
      console.error('Error joining loyalty program:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Could not join the program. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-primary/10 rounded-lg border border-primary/20">
        <Check className="h-5 w-5 text-primary" />
        <span className="font-medium text-primary">You're in! 50 points added 🎉</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
      <Input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        disabled={isSubmitting}
        className="flex-1"
        maxLength={255}
      />
      <Button 
        type="submit" 
        size="lg"
        disabled={isSubmitting}
        className="sm:w-auto w-full"
        data-gtm-id="home-loyalty-signup-submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Joining...
          </>
        ) : (
          'Get 50 Points Free'
        )}
      </Button>
    </form>
  );
};

export default LoyaltySignupForm;
